import { Accordion, Badge } from "components/common"
import React, { useState } from "react";
import { formatDate, formatNumber } from "helpers/utils";
import DocumentAttachment from "components/common/upload/DocumentAttachment";
import { MdLock, MdOutlineTransferWithinAStation, MdVerified } from "react-icons/md";
import UploadDocumentDirect from "components/common/upload/UploadDocumentDirect";
import { useDispatch } from "react-redux";
import { removeDocument, uploadDocument } from "redux/crm/project/waiverSlice";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { CRM_WAIVER_FORM_ISO } from "app_config";
import PropTypes from "prop-types";

const WaiverDetailForm = ({ waiver }) => {
  const [activeKey, setActiveKey] = useState(["0", "2", "3", "4", "5", "6", "7", "8"])
  const { project, buildings, documents, permissions } = waiver
  const {
    allow_view_document,
    allow_upload_document,
    allow_download_document,
    allow_remove_document
  } = permissions || {}

  const dispatch = useDispatch()

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  const handleUploadDocument = (data) => {
    try {
      dispatch(uploadDocument({
        projectId: project.id,
        waiverId: waiver.id,
        data
      }))
    }
    catch (error) {
      toast.error(error.message)
    }
  }

  const handleRemoveDocument = (documentId) => {
    dispatch(removeDocument({
      projectId: project.id,
      waiverId: waiver.id,
      documentId
    }))
  }

  const checkRow = (label, checked) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2">
      <div className="tw-col-span-12 sm:tw-col-span-4">{label}</div>
      <div className="tw-col-span-12 sm:tw-col-span-8">
        <input type="checkbox" checked={checked || false} readOnly />
      </div>
    </div>
  );

  const checkItem = (label, checked) => (
    <label className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1">
      <input type="checkbox" checked={checked || false} readOnly />
      {label}
    </label>
  );

  const valueRow = (label, value) => (
    <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2">
      <div className="tw-col-span-12 sm:tw-col-span-4">{label}</div>
      <div className="tw-col-span-12 sm:tw-col-span-8">{value}</div>
    </div>
  );

  const CELL_BORDER = "tw-border tw-border-solid tw-border-slate-200";

  return (
    <>
      <Accordion
        id="waiver-detail"
        className="tw-text-sm"
        activeKeys={activeKey}
        onChange={handleAccordionSelect}
      >
        {/* 1. Project Information */}
        <Accordion.Item eventKey="0" header="1. Project Information">
          <div className="tw-m-2 tw-p-3">
            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2">
              <div className="tw-col-span-12 md:tw-col-span-6">
                <div className="tw-flex tw-gap-2">
                  <div className="tw-w-1/3">Project name:</div>
                  <div className="tw-flex-1">
                    <Link to={`/crm/projects/${project?.id}`}>{project?.name}</Link>
                  </div>
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <div className="tw-flex tw-gap-2">
                  <div>Customer name:</div>
                  <div className="tw-flex-1">
                    <Link to={`/crm/customers/${project?.customer?.id}`}>{project?.customer?.name}</Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="tw-flex tw-gap-2 tw-mb-2">
              <div className="tw-w-1/6">Quotation / Job No:</div>
              <div className="tw-flex-1">
                <Link to={`/crm/projects/${project?.id}`}>{project?.j_number ? project?.j_number : project?.q_number}</Link>
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2">
              <div className="tw-col-span-12 md:tw-col-span-4 tw-flex tw-gap-2">
                <div className="tw-w-1/2">Main contract value:</div>
                <div className="tw-flex-1">
                  <span className="tw-text-blue-600">${formatNumber(waiver.main_contract_value)}</span>
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-4 tw-flex tw-gap-2">
                <div className="tw-w-1/2">1st Addendum value:</div>
                <div className="tw-flex-1">
                  <span className="tw-text-blue-600">${formatNumber(waiver.addendum_value_1st)}</span>
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-4 tw-flex tw-gap-2">
                <div className="tw-w-5/12">2st Addendum value:</div>
                <div className="tw-flex-1">
                  <span className="tw-text-blue-600">${formatNumber(waiver.addendum_value_2st)}</span>
                </div>
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-12 tw-gap-2 tw-mb-2">
              <div className="tw-col-span-12 md:tw-col-span-4 tw-flex tw-gap-2">
                <div className="tw-w-1/2">Main contract weight:</div>
                <div className="tw-flex-1">
                  <span className="tw-text-blue-600">{formatNumber(waiver.main_contract_weight)} M.T</span>
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-4 tw-flex tw-gap-2">
                <div className="tw-w-1/2">1st Addendum weight:</div>
                <div className="tw-flex-1">
                  <span className="tw-text-blue-600">${formatNumber(waiver.addendum_weight_1st)} M.T</span>
                </div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-4 tw-flex tw-gap-2">
                <div className="tw-w-5/12">2st Addendum weight:</div>
                <div className="tw-flex-1">
                  <span className="tw-text-blue-600">${formatNumber(waiver.addendum_weight_2st)} M.T</span>
                </div>
              </div>
            </div>
          </div>
        </Accordion.Item>

        {/* 2. Payment history (for repeat customer) */}
        <Accordion.Item eventKey="1" header="2. Payment history (for repeat customer)">
          <div className="tw-m-2 tw-p-3">
            <div className="tw-text-blue-600" dangerouslySetInnerHTML={{ __html: waiver.payment_history }}></div>
          </div>
        </Accordion.Item>

        {/* 3. Job Status: */}
        <Accordion.Item eventKey="2" header="3. Job Status:">
          <div className="tw-m-2 tw-p-3">
            {checkRow('Contract', waiver.job_status_contract)}
            {checkRow('LOI', waiver.job_status_loi)}
            {checkRow('JAF', waiver.job_status_jaf)}
            {checkRow('BOM & JAF comparison ( For releasing shipment waiver)', waiver.job_status_bom_jaf_comparison)}
            {valueRow('Others', (
              <span className="tw-text-blue-600" dangerouslySetInnerHTML={{ __html: waiver.job_status_other }}></span>
            ))}
          </div>
        </Accordion.Item>

        {/* 4. Payment term: */}
        <Accordion.Item eventKey="3" header="4. Payment term:">
          <div className="tw-m-2 tw-p-3">
            {valueRow('1. Down payment', <span className="tw-text-blue-600">{waiver.payment_term_dp}%</span>)}
            {valueRow('2. Second payment after delivery of M/F', <span className="tw-text-blue-600">{waiver.payment_term_second}%</span>)}
            {valueRow('3. Third payment after deilivery of sheeting', <span className="tw-text-blue-600">{waiver.payment_term_third}%</span>)}
            {valueRow('4. Four payment after completion of erection', <span className="tw-text-blue-600">{waiver.payment_term_four}%</span>)}
            {valueRow('5. Final payment', <span className="tw-text-blue-600">{waiver.payment_term_final}%</span>)}
            {valueRow('Percentage of receiving:', <span className="tw-text-blue-600">{waiver.percentage_of_receiving || 0.0}%</span>)}
            {valueRow('Others', (
              <span className="tw-text-blue-600" dangerouslySetInnerHTML={{ __html: waiver.payment_term_other }}></span>
            ))}
          </div>
        </Accordion.Item>

        {/* 5. Release for : */}
        <Accordion.Item eventKey="4" header="5. Release for :">
          <div className="tw-m-2 tw-p-3">
            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
              <div className="tw-col-span-12 md:tw-col-span-6">
                {checkItem('Anchor bolt delivery', waiver.released_for_anchor_bolt)}
                {checkItem('Fabrication', waiver.released_for_fabrication)}
                {checkItem('Loading', waiver.released_for_loading)}
                {checkItem('Shipment', waiver.released_for_shipment)}
                {checkItem('Order material', waiver.released_for_order_material)}
                <div className="tw-text-blue-600">{waiver.released_for_order_material}</div>
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                {checkItem('Approval dwgs & Estimate', waiver.released_for_apd_estimate)}
                {checkItem('Shop dwgs', waiver.released_for_shop)}
                {checkItem('Others (specify)', waiver.released_for_other)}

                <span className="tw-text-blue-600" dangerouslySetInnerHTML={{ __html: waiver.released_for_other_specify }}></span>
              </div>
            </div>
          </div>
        </Accordion.Item>

        {/* 6. Reason for waiver:*/}
        <Accordion.Item eventKey="5" header="6. Reason for waiver:">
          <div className="tw-m-2 tw-p-3">
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-border-collapse tw-text-sm">
                <thead>
                  <tr className="tw-bg-slate-50">
                    <th className={`${CELL_BORDER} tw-px-2 tw-py-1.5 tw-text-left`} width="5%">No.</th>
                    <th className={`${CELL_BORDER} tw-px-2 tw-py-1.5 tw-text-left`} width="10%">Building</th>
                    <th className={`${CELL_BORDER} tw-px-2 tw-py-1.5 tw-text-left`} width="85%">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {buildings && buildings.map((building, index) => (
                    <tr key={index} className="odd:tw-bg-white even:tw-bg-slate-50 hover:tw-bg-slate-100">
                      <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>{index + 1}</td>
                      <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>{building.name}</td>
                      <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                        <span dangerouslySetInnerHTML={{ __html: building.reason }}></span>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="3" className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-font-semibold tw-text-blue-600">GM Operation comment:</div>
                      <div className="tw-ml-3 tw-mt-1">
                        {waiver.general_manager_operation_remark ? (
                          <div className="tw-bg-yellow-50 tw-p-3 tw-rounded-sm" dangerouslySetInnerHTML={{ __html: waiver.general_manager_operation_remark }}></div>
                        ) : "N/A"}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="3" className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-font-semibold tw-text-blue-600">CSD comment:</div>
                      <div className="tw-ml-3 tw-mt-1">
                        {waiver.csd_remark ? (
                          <div className="tw-bg-yellow-50 tw-p-3 tw-rounded-sm" dangerouslySetInnerHTML={{ __html: waiver.csd_remark }}></div>
                        ) : "N/A"}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="tw-bg-white tw-rounded tw-border tw-border-solid tw-border-slate-200 tw-p-4 tw-mt-2">
              <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2">
                <div className="tw-col-span-12 md:tw-col-span-6 tw-flex tw-gap-2">
                  <div className="tw-w-1/3 tw-font-semibold">Waiver value</div>
                  <div className="tw-flex-1 tw-text-blue-600">${formatNumber(waiver.waiver_value)}</div>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-6 tw-flex tw-gap-2">
                  <div className="tw-w-1/3 tw-font-semibold">Waiver weight</div>
                  <div className="tw-flex-1 tw-text-blue-600">{formatNumber(waiver.waiver_weight)} M.T</div>
                </div>
              </div>

              <div className="tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2">
                <div className="tw-col-span-12 sm:tw-col-span-2">CSE incharges</div>
                <div className="tw-col-span-12 sm:tw-col-span-10 tw-text-blue-600">
                  {waiver?.cse_incharge && waiver?.cse_incharge.map((item, index) =>
                    <Badge variant="primary" className="tw-mr-1 tw-rounded-xl" key={index}>{item}</Badge>
                  )}
                </div>
              </div>

              <div className="tw-flex tw-justify-center">
                <div className="tw-border-b tw-border-dashed tw-border-slate-200 tw-my-3 tw-w-full"></div>
              </div>

              <div className="tw-grid tw-grid-cols-12 tw-gap-3">
                <div className="tw-col-span-12 md:tw-col-span-4 md:tw-border-r md:tw-border-solid md:tw-border-slate-200">
                  <h6 className="tw-font-semibold tw-mb-2 tw-text-cyan-600"><MdVerified /> Verified</h6>
                  <div className="tw-ml-3">
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">By</div>
                      <div className="tw-flex-1 tw-text-blue-600">{waiver.verify?.by}</div>
                    </div>
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">Date</div>
                      <div className="tw-flex-1 tw-text-blue-600">{formatDate(waiver.verify?.date)}</div>
                    </div>
                    <div className="tw-flex tw-mb-2 tw-text-red-600">
                      <div className="tw-flex-1">Needed to reverify by Sales</div>
                      <div className="tw-flex-1">
                        <input type="checkbox" checked={waiver.verify?.need_reverify_by_sales || false} readOnly />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4 md:tw-border-r md:tw-border-solid md:tw-border-slate-200">
                  <h6 className="tw-font-semibold tw-mb-2 tw-text-yellow-500"><MdOutlineTransferWithinAStation /> Transfered</h6>
                  <div className="tw-ml-3">
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">To CSE</div>
                      <div className="tw-flex-1">
                        <input type="checkbox" checked={waiver.transfer?.transfered_to_cse_incharge || false} readOnly />
                      </div>
                    </div>
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">By</div>
                      <div className="tw-flex-1 tw-text-blue-600">{waiver.transfer?.by}</div>
                    </div>
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">Date</div>
                      <div className="tw-flex-1 tw-text-blue-600">{formatDate(waiver.transfer?.date)}</div>
                    </div>
                  </div>
                </div>
                <div className="tw-col-span-12 md:tw-col-span-4">
                  <h6 className="tw-font-semibold tw-mb-2 tw-text-green-600"><MdLock /> Closed</h6>
                  <div className="tw-ml-3">
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">By</div>
                      <div className="tw-flex-1 tw-text-blue-600">{waiver?.close?.by}</div>
                    </div>
                    <div className="tw-flex tw-mb-2">
                      <div className="tw-flex-1">Date</div>
                      <div className="tw-flex-1 tw-text-blue-600">{formatDate(waiver?.close?.date)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Accordion.Item>

        {/* 7. Commitment date to clear waiver: */}
        <Accordion.Item
          eventKey="6"
          header={(
            <span className="tw-text-slate-900">
              <span>7. Commitment date to clear waiver:</span>
              <span className="tw-text-blue-600 tw-ml-3">{formatDate(waiver.commitment_date)}</span>
            </span>
          )}
        >
          <div className="tw-m-2 tw-p-3">
            <div className="tw-overflow-x-auto">
              <table className="tw-w-full tw-border-collapse tw-text-sm">
                <tbody>
                  <tr className="tw-text-center">
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`} width="20%">Requestor</td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`} width="20%">
                      <div className="tw-p-1">
                        General Manager/ Chief Rep
                        <div className="tw-text-blue-600 tw-m-0 tw-p-0">(Approver)</div>
                      </div>
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`} width="20%">
                      <div className="tw-p-1">
                        GM - Operation
                        <div className="tw-text-blue-600 tw-m-0 tw-p-0">(Approver)</div>
                      </div>
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`} width="20%">
                      <div className="tw-p-1">
                        Credit Controller
                        <div className="tw-text-blue-600 tw-m-0 tw-p-0">(Verifier)</div>
                      </div>
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`} width="20%">
                      <div className="tw-p-1">
                        Finance Controller
                        <div className="tw-text-blue-600 tw-m-0 tw-p-0">(Approver)</div>
                      </div>
                    </td>
                  </tr>
                  <tr className="tw-text-center">
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-text-blue-600 tw-mt-1">{waiver.request?.by}</div>
                      <div>Date: <span className="tw-text-blue-600">{waiver.request?.date}</span></div>
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-text-blue-600 tw-mt-1">{waiver.general_sales_manager?.by}</div>
                      {waiver.general_sales_manager?.date && (
                        <div>Date: <span className="tw-text-blue-600">{waiver.general_sales_manager.date}</span></div>
                      )}
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-text-blue-600 tw-mt-1">{waiver.general_manager_operation?.by}</div>
                      {waiver.general_manager_operation?.date && (
                        <div>Date: <span className="tw-text-blue-600">{waiver.general_manager_operation.date}</span></div>
                      )}
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-text-blue-600 tw-mt-1">{waiver.credit_controller?.by}</div>
                      {waiver.credit_controller?.date && (
                        <div>Date: <span className="tw-text-blue-600">{waiver.credit_controller.date}</span></div>
                      )}
                    </td>
                    <td className={`${CELL_BORDER} tw-px-2 tw-py-1.5`}>
                      <div className="tw-text-blue-600 tw-mt-1">{waiver.finance_controller?.by}</div>
                      {waiver.finance_controller?.date && (
                        <div>Date: <span className="tw-text-blue-600">{waiver.finance_controller.date}</span></div>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </Accordion.Item>

        {/* Attachemnts */}
        <Accordion.Item eventKey="7" header="Attachemnts">
          <div className="tw-m-2 tw-p-3">
            <div className="tw-grid tw-grid-cols-12 tw-gap-4">
              <div className="tw-col-span-12 md:tw-col-span-6">
                {allow_upload_document && (
                  <UploadDocumentDirect uploadDocument={(data) => handleUploadDocument(data)} />
                )}
              </div>
              <div className="tw-col-span-12 md:tw-col-span-6">
                <div className="tw-mt-4">
                  {documents && documents.map(attachment => (
                    <DocumentAttachment
                      {...attachment}
                      key={attachment.id}
                      isTable
                      allowView={allow_view_document}
                      allowRemove={allow_remove_document}
                      allowDownload={allow_download_document}
                      handleRemoveAttachment={(documentId) => handleRemoveDocument(documentId)}
                    />
                  ))
                  }
                </div>
              </div>
            </div>
          </div>
        </Accordion.Item>
      </Accordion>
      <div className="tw-my-2 tw-text-sm">{CRM_WAIVER_FORM_ISO}</div>
    </>
  )
};

WaiverDetailForm.propTypes = {
  waiver: PropTypes.shape({
    id: PropTypes.number.isRequired,
    main_contract_value: PropTypes.number,
    addendum_value_1st: PropTypes.number,
    addendum_value_2st: PropTypes.number,
    main_contract_weight: PropTypes.number,
    addendum_weight_1st: PropTypes.number,
    addendum_weight_2st: PropTypes.number,
    payment_history: PropTypes.string,
    job_status_contract: PropTypes.string,
    job_status_loi: PropTypes.string,
    job_status_jaf: PropTypes.string,
    job_status_bom_jaf_comparison: PropTypes.string,
    job_status_other: PropTypes.string,
    payment_term_dp: PropTypes.string,
    payment_term_second: PropTypes.string,
    payment_term_third: PropTypes.string,
    payment_term_four: PropTypes.string,
    payment_term_final: PropTypes.string,
    percentage_of_receiving: PropTypes.string,
    payment_term_other: PropTypes.string,
    released_for_anchor_bolt: PropTypes.string,
    released_for_fabrication: PropTypes.string,
    released_for_shipment: PropTypes.string,
    released_for_loading: PropTypes.string,
    released_for_order_material: PropTypes.string,
    released_for_other: PropTypes.string,
    released_for_other_specify: PropTypes.string,
    released_for_shop: PropTypes.string,
    released_for_apd_estimate: PropTypes.string,
    general_manager_operation_remark: PropTypes.string,
    csd_remark: PropTypes.string,
    waiver_value: PropTypes.number,
    waiver_weight: PropTypes.number,
    cse_incharge: PropTypes.string,
    verify: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date),
      need_reverify_by_sales: PropTypes.bool
    }),
    transfer: PropTypes.shape({
      transfered_to_cse_incharge: PropTypes.bool,
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)
    }),
    close: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)
    }),
    commitment_date: PropTypes.instanceOf(Date),
    request: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)

    }),
    general_sales_manager: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)
    }),
    general_manager_operation: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)
    }),
    credit_controller: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)
    }),
    finance_controller: PropTypes.shape({
      by: PropTypes.string,
      date: PropTypes.instanceOf(Date)
    }),

    project: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      q_number: PropTypes.string,
      j_number: PropTypes.string,
      customer: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    }),
    buildings: PropTypes.object.isRequired,
    documents: PropTypes.object.isRequired,
    permissions: PropTypes.object.isRequired
  })
};

export default WaiverDetailForm;
